// Logique pure des écrans d'analyse de Jamora App (lot 27-9) : période, évolution, pages numérotées, tri, compteurs d'onglets.
// Aucune dépendance à React ni au navigateur : tout est testé dans jamoraAnalysis.test.js.

export const MAX_SPAN_DAYS = 400; // même plafond que le contrat 0.8.0
export const DEFAULT_PRESET = "30d";
export const PERIOD_PRESETS = [
  ["today", "Aujourd'hui"], ["7d", "7 derniers jours"], ["30d", "30 derniers jours"], ["90d", "90 derniers jours"],
  ["month", "Ce mois-ci"], ["year", "Cette année"], ["custom", "Personnalisée…"],
];
export const PAGE_SIZES = [25, 50, 100];

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const toDate = (iso) => new Date(`${iso}T00:00:00Z`);
const toIso = (date) => date.toISOString().slice(0, 10);

export function addDays(iso, days) {
  const date = toDate(iso);
  date.setUTCDate(date.getUTCDate() + days);
  return toIso(date);
}

export const spanDays = (from, to) => Math.round((toDate(to) - toDate(from)) / 86400000) + 1;

// Message (français) si la période personnalisée est refusable, sinon "".
export function customError(from, to) {
  if (!from || !to) return "Choisissez une date de début et une date de fin.";
  if (!DAY.test(from) || !DAY.test(to) || Number.isNaN(toDate(from).getTime()) || Number.isNaN(toDate(to).getTime())) return "Date invalide.";
  if (from > to) return "La date de début doit précéder la date de fin.";
  if (spanDays(from, to) > MAX_SPAN_DAYS) return `La période ne peut pas dépasser ${MAX_SPAN_DAYS} jours.`;
  return "";
}

// { from, to } d'un raccourci, `today` étant le jour de l'entreprise (AAAA-MM-JJ). Une période personnalisée invalide retombe sur 30 jours.
export function rangeOf(preset, today, custom = {}) {
  switch (preset) {
    case "today": return { from: today, to: today };
    case "7d": return { from: addDays(today, -6), to: today };
    case "90d": return { from: addDays(today, -89), to: today };
    case "month": return { from: `${today.slice(0, 8)}01`, to: today };
    case "year": return { from: `${today.slice(0, 4)}-01-01`, to: today };
    case "custom": return customError(custom.from, custom.to) ? rangeOf(DEFAULT_PRESET, today) : { from: custom.from, to: custom.to };
    default: return { from: addDays(today, -29), to: today };
  }
}

export function presetLabel(preset) {
  return (PERIOD_PRESETS.find(([id]) => id === preset) || PERIOD_PRESETS[2])[1];
}

// Évolution par rapport à la période précédente. Pas de comparaison (null) quand l'une des valeurs manque ou que l'ancienne vaut 0 :
// un pourcentage sur zéro n'a pas de sens et on ne l'invente pas.
export function formatDelta(value, previous) {
  if (value === null || value === undefined || previous === null || previous === undefined || previous === 0) return null;
  const percent = Math.round(((value - previous) / Math.abs(previous)) * 100);
  if (percent === 0) return { text: "stable", direction: "flat", percent: 0 };
  return { text: `${percent > 0 ? "+" : "−"}${Math.abs(percent)} %`, direction: percent > 0 ? "up" : "down", percent };
}

// Fenêtre d'une page numérotée : « 51–100 sur 1 284 », page 2 sur 26.
export function pageWindow(offset, total, limit) {
  const size = Math.max(1, limit);
  const count = Math.max(0, total || 0);
  const pages = Math.max(1, Math.ceil(count / size));
  const page = Math.min(pages, Math.floor(Math.max(0, offset) / size) + 1);
  const start = count ? (page - 1) * size : 0;
  return { from: count ? start + 1 : 0, to: Math.min(start + size, count), page, pages, hasPrev: page > 1, hasNext: page < pages, lastOffset: (pages - 1) * size };
}

// Après un filtre ou une suppression, un décalage qui dépasse la fin revient à la dernière page existante.
export function clampOffset(offset, total, limit) {
  if (!total || offset < total) return Math.max(0, offset);
  return pageWindow(offset, total, limit).lastOffset;
}

// Tri d'une colonne : aucun → croissant → décroissant → aucun.
export function nextOrdering(current, key) {
  if (current === key) return `-${key}`;
  if (current === `-${key}`) return "";
  return key;
}

export const orderingDirection = (current, key) => (current === key ? "ascending" : current === `-${key}` ? "descending" : "none");

// Paramètres d'une liste pour l'API : tout ce qui est vide disparaît ; `offset` est toujours envoyé (page numérotée).
export function listParams({ offset = 0, limit = 50, ordering = "", range = null, filters = {}, search = "" }) {
  const params = { offset, limit };
  if (ordering) params.ordering = ordering;
  if (range) { params.date_from = range.from; params.date_to = range.to; }
  if (search) params.search = search;
  Object.entries(filters).forEach(([key, value]) => { if (value !== "" && value !== null && value !== undefined) params[key] = value; });
  return params;
}

// Export CSV (lot 27-9g) : la requête reprend EXACTEMENT ce que la liste affiche (filtres, recherche, période, tri) mais sans page ; les colonnes sont celles de
// l'écran dont l'identifiant est une vraie donnée de la ligne (une colonne calculée à l'écran n'est pas exportée). Jamais plus que ce que la liste montre.
export function exportColumns(columns, row) {
  if (!row) return [];
  return columns.filter((c) => typeof c.label === "string" && c.label && c.id in row).map((c) => [c.id, c.label.replace(/\|/g, "/")]);
}

export function exportQuery({ range = null, filters = {}, search = "", ordering = "" }, columns) {
  const params = new URLSearchParams();
  const { offset, limit, ...rest } = listParams({ range, filters, search, ordering }); // eslint-disable-line no-unused-vars
  Object.entries(rest).forEach(([key, value]) => params.append(key, value));
  columns.forEach(([key, label]) => params.append("col", `${key}|${label}`));
  return params.toString();
}

// Onglets avec compteurs : [code, libellé, nombre|null]. `labels` : { CODE: "Libellé" }. Un code que le libellé ne connaît pas garde son code.
// Le premier onglet (« Toutes ») porte la somme ; null quand Jamora n'a pas envoyé de compteurs (jamais un zéro inventé).
export function tabsWithCounts(allLabel, labels, facet) {
  if (!Array.isArray(facet)) return [["", allLabel, null], ...Object.entries(labels).map(([code, label]) => [code, label, null])];
  const byCode = new Map(facet.map((row) => [row.code, row.total]));
  const known = Object.entries(labels).map(([code, label]) => [code, label, byCode.get(code) ?? 0]);
  const unknown = facet.filter((row) => !(row.code in labels)).map((row) => [row.code ?? "", row.code ?? "Sans valeur", row.total]);
  return [["", allLabel, facet.reduce((sum, row) => sum + row.total, 0)], ...known, ...unknown];
}

export const facetOf = (summary, group, field) => summary?.facets?.[group]?.[field];
export const facetTotal = (facet) => (Array.isArray(facet) ? facet.reduce((sum, row) => sum + row.total, 0) : null);
